/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { isConfluenceSlideUrl } from '@atlaskit/editor-card-provider/url-checkers';
import type { NewCoreIconProps } from '@atlaskit/icon';
import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const SLIDE_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('slide');

/**
 * Experience manifest for a slide embed.
 * Register with getNativeEmbedFacade().registerExperience(SlideExperienceManifest)
 * to obtain a Experience for EmbedFrameUI.
 */
export const SlideExperienceManifest: {
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => boolean;
	readonly isUrlMatch: (url: string) => boolean;
	readonly parameterDefaults?: ExperienceManifest['parameterDefaults'];
	readonly queryParams: {
		readonly disableCanvasScroll: 'true';
	};
	readonly supportedFeatures: Set<string>;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	id: SLIDE_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {
		// TODO: Handle slide-specific commands if needed
	},
	isUrlMatch: (url: string): boolean =>
		expValEqualsNoExposure('cc-mui-slides-experiment', 'isEnabled', true) &&
		isConfluenceSlideUrl(url),
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	queryParams: { disableCanvasScroll: 'true' },
	isActive: (): boolean => expValEqualsNoExposure('cc-mui-slides-experiment', 'isEnabled', true),
	// Slides have a 16:9 canvas; set the default embed dimensions to 16:10 with a little extra height cause it looks nicer.
	parameterDefaults: {
		width: 960,
		height: 600,
	},
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			items: [
				// BUILTIN_TOOLBAR_KEYS.ASK_ROVO, // Removed until correct context from embed is provided https://product-fabric.atlassian.net/browse/CNS-29056
				BUILTIN_TOOLBAR_KEYS.SUMMARISE,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.EDIT_URL,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay animationDisabled={props.animationDisabled} testId={props.testId} />
		),
		titleIcon: AiGenerativeSlidesIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
